import axios from 'axios';
import { Vehicle, VehicleFilters, VehicleListResponse, VehicleFilterOptions } from '../types/Vehicle';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

class VehicleService {
  private handleError(error: any): Error {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.error || error.response.data?.message || 'An error occurred';
      return new Error(message);
    } else if (error.request) {
      // Request made but no response received
      return new Error('No response from server. Please check your connection.');
    } else {
      // Something else happened
      return new Error(error.message || 'An unexpected error occurred');
    }
  }

  async getVehiclesPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters: VehicleFilters = {}
  ): Promise<VehicleListResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== undefined && value !== '')
        )
      });

      const response = await axios.get(`${BASE_URL}/vehicles?${params}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getVehicleById(id: number): Promise<Vehicle> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createVehicle(data: Partial<Vehicle>): Promise<Vehicle> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateVehicle(id: number, data: Partial<Vehicle>): Promise<Vehicle> {
    try {
      const response = await axios.put(`${BASE_URL}/vehicles/${id}`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getVehicleFilterOptions(): Promise<VehicleFilterOptions> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/filter-options`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getVehicleProfilePreview(vehicleId: number): Promise<any> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/${vehicleId}/profile-preview`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

export default new VehicleService();

